// Route: /tools/visual-explainer — список Visual Analysis «Картотека».
// Старый адрес /tools/visuals остался редиректом (routes/tools/visuals/index.tsx).
// URL state: ?category=graph|formula|..., ?status=..., ?demo=loading|empty|error|processing
//
// См. docs/ui-spec/tools-visuals.list.{desktop,mobile}.md

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, EmptyState, ErrorState, SectionHeader, Skeleton } from "@studyhub/ui";
import { IconCategory, IconPhotoScan, IconPlus } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { SECTION_TINT } from "@/components/navigation/section-icons";
import { LIST_PAGE_SHELL, ListToolbar } from "@/components/shared/entity-list";
import { FilterDropdown, filterDropdownTrigger } from "@/components/shared/filter-pill";
import { PagePendingFallback } from "@/components/shared/page-pending-fallback";
import {
	SectionIntro,
	SectionIntroReopen,
	useSectionIntro,
} from "@/components/shared/section-intro";
import {
	useVisuals,
	type VisualCategory,
	type VisualStatus,
} from "@/components/visual-explainer/_api";
import { UploadDialog } from "@/components/visual-explainer/upload-dialog";
import { VisualList } from "@/components/visual-explainer/visual-list";
import { useArtifactWatching } from "@/hooks/use-artifact-watching";
import { markArtifactSeenKey } from "@/hooks/use-mark-artifact-seen";
import { useViewMode } from "@/hooks/use-view-mode";

const searchSchema = z.object({
	category: z
		.enum(["problem", "graph", "formula", "handwriting", "diagram", "photo", "other"])
		.optional(),
	status: z.enum(["queued", "analyzing", "ready", "failed"]).optional(),
	q: z.string().optional(),
	demo: z.enum(["default", "loading", "empty", "error", "processing"]).optional(),
	/** Авто-открыть диалог загрузки изображения (точка входа с дашборда). */
	create: z.boolean().optional(),
});

const CATEGORIES: VisualCategory[] = [
	"problem",
	"graph",
	"formula",
	"handwriting",
	"diagram",
	"photo",
	"other",
];

export const Route = createFileRoute("/tools/visual-explainer/")({
	validateSearch: tolerantSearch(searchSchema),
	component: VisualsListPage,
	pendingComponent: PagePendingFallback,
});

function VisualsListPage(): React.ReactElement {
	const { t } = useTranslation("visual-explainer");
	const intro = useSectionIntro("visual-explainer");
	const search = Route.useSearch();
	const navigate = useNavigate({ from: Route.fullPath });
	const [uploadOpen, setUploadOpen] = useState(false);
	const [view, setView] = useViewMode("visuals");
	const visualsQuery = useVisuals();

	// Авто-открытие диалога загрузки при переходе с дашборда (?create=true).
	// Сразу очищаем флаг из URL — иначе reload/back повторно откроют диалог.
	useEffect(() => {
		if (!search.create) return;
		setUploadOpen(true);
		void navigate({ search: (prev) => ({ ...prev, create: undefined }), replace: true });
	}, [search.create, navigate]);

	const isLoading = search.demo === "loading" || visualsQuery.isLoading;
	const isError = search.demo === "error" || visualsQuery.isError;
	const isEmpty = search.demo === "empty";

	const items = useMemo(() => {
		if (isEmpty) return [];
		let list = visualsQuery.data?.items ?? [];
		if (search.demo === "processing") {
			list = list.map((v) => ({ ...v, status: "analyzing" as VisualStatus }));
		}
		if (search.category) list = list.filter((v) => v.category === search.category);
		if (search.status) list = list.filter((v) => v.status === search.status);
		if (search.q) {
			const q = search.q.toLowerCase();
			list = list.filter((v) => v.title.toLowerCase().includes(q));
		}
		return list;
	}, [visualsQuery.data, isEmpty, search.demo, search.category, search.status, search.q]);

	// Presence-aware: queued/analyzing на списке → mute notify.
	const pendingVisualWatchKeys = useMemo(
		() =>
			items
				.filter((v) => v.status === "queued" || v.status === "analyzing")
				.map((v) => markArtifactSeenKey("visual", v.id)),
		[items],
	);
	useArtifactWatching(pendingVisualWatchKeys);

	const categoryValue = search.category ?? "all";
	const categoryOptions = [
		{ value: "all", label: t("filter.all") },
		...CATEGORIES.map((c) => ({ value: c, label: t(`category.${c}`) })),
	];
	const categoryTrigger = filterDropdownTrigger(
		categoryOptions,
		categoryValue,
		t("filter.category"),
	);
	const categoryFilter = (stacked: boolean) => (
		<FilterDropdown
			stacked={stacked}
			ariaLabel={t("filter.category")}
			icon={<IconCategory size={14} aria-hidden />}
			triggerLabel={categoryTrigger.label}
			active={categoryTrigger.active}
			value={categoryValue}
			options={categoryOptions}
			onChange={(v) =>
				void navigate({
					search: (prev) => ({
						...prev,
						category: v === "all" ? undefined : (v as VisualCategory),
					}),
					replace: true,
				})
			}
		/>
	);

	return (
		<div className={LIST_PAGE_SHELL}>
			<SectionHeader
				icon={<IconPhotoScan />}
				category="image"
				tint={SECTION_TINT.visuals}
				title={t("title")}
				description={t("subtitle")}
				actions={
					<>
						<SectionIntroReopen intro={intro} />
						<Button onClick={() => setUploadOpen(true)} aria-label={t("newCta")}>
							<IconPlus size={16} />
							<span className="hidden sm:inline">{t("newCta")}</span>
						</Button>
					</>
				}
			/>

			{/* Баннер-объяснение раздела: текст из манифеста модуля, состояние
			    «свёрнут» — серверное (см. section-intro.tsx). */}
			<SectionIntro intro={intro} />

			{!isError && !isLoading && (
				<ListToolbar
					search={{
						value: search.q ?? "",
						onChange: (q) =>
							void navigate({ search: (prev) => ({ ...prev, q: q || undefined }), replace: true }),
						placeholder: t("list.search"),
					}}
					filters={categoryFilter(false)}
					filtersSheet={categoryFilter(true)}
					filtersActive={!!search.category}
					view={view}
					onViewChange={setView}
				/>
			)}

			{isLoading ? (
				/* Media thumbnail skeleton — same denser cols as VisualList; gap-grid. */
				<div className="grid grid-cols-2 gap-grid sm:grid-cols-3 lg:grid-cols-4">
					{Array.from({ length: 8 }).map((_, i) => (
						<div
							key={i}
							className="overflow-hidden rounded-[var(--radius-card)] border border-border bg-card shadow-e1"
						>
							<Skeleton className="aspect-[4/3] rounded-none" />
							<div className="p-3">
								<Skeleton className="h-4 w-3/4" />
							</div>
						</div>
					))}
				</div>
			) : isError ? (
				<ErrorState
					title={t("list.errorTitle")}
					description={t("list.errorSubtitle")}
					action={
						<Button variant="outline" onClick={() => void visualsQuery.refetch()}>
							{t("list.retry")}
						</Button>
					}
				/>
			) : items.length === 0 ? (
				<EmptyState
					icon={<IconPhotoScan stroke={1.6} />}
					title={t("list.empty.title")}
					description={t("list.empty.subtitle")}
					action={
						<Button onClick={() => setUploadOpen(true)}>
							<IconPlus size={16} />
							{t("list.empty.cta")}
						</Button>
					}
				/>
			) : (
				<VisualList items={items} view={view} />
			)}

			<UploadDialog open={uploadOpen} onOpenChange={setUploadOpen} />
		</div>
	);
}
